<template>
  <div ref="step" class="mx-auto w-full max-w-[650px] overflow-y-auto px-6 pb-5 text-center">
    <div class="border-bdr-primary mb-5 flex h-9 items-center gap-2 rounded-full border px-4">
      <p class="text-body-default-m">{{ t(reverseMode) }}</p>
      <p class="text-body-default truncate font-medium">
        {{ values[reverseMode]?.name
        }}<span v-if="values[reverseMode]?.attention">, att. {{ values[reverseMode]?.attention }}</span
        >, {{ values[reverseMode]?.street1 }} {{ values[reverseMode]?.street2 }}, {{ values[reverseMode]?.zip_code }},
        {{ values[reverseMode]?.city }},
        {{ getCountryName(values[reverseMode]?.country) }}
      </p>
      <Button v-if="!isDFM" type="button" :variant="ButtonVariant.Ghost" @click="addressModalOpen = true">{{
        t("change")
      }}</Button>
    </div>
    <div class="flex flex-col gap-4">
      <AutoComplete
        v-model="selectedContact"
        autocomplete="one-time-code"
        class="w-full"
        name="saved_receiver_input"
        :loading="loadingContacts"
        :empty-text="t('no_contacts_found')"
        :label="t('search_saved_contacts')"
        :placeholder="t('search_saved_contacts_placeholder')"
        :options="contacts"
        @delete="(val) => deleteContact(val)"
      />
    </div>
    <div class="py-7">
      <Divider :text="t('enter_address_manually')" />
    </div>
    <SettingsToggle
      v-if="showBusinessToggle"
      id="business"
      :name="`${mode}.business`"
      :text="t(`${mode}_is_business`)"
    />
    <div class="grid grid-cols-2 gap-4" :class="{ 'mt-4': showBusinessToggle }">
      <DropdownCountriesWithPrices :id="'country'" :label="t('country')" :name="`${mode}.country`" :type="type" />
      <div>
        <InputText
          v-if="stateSettings"
          id="state"
          autocomplete="one-time-code"
          :name="`${mode}.state`"
          :label="stateSettings.label"
          :placeholder="stateSettings.label"
        />
      </div>
      <template v-if="isBusiness">
        <InputAddressGoogle
          id="name"
          :name="`${mode}.name`"
          :label="t('company_name')"
          :placeholder="t('name')"
          :country="country"
          :highlight-house-number="false"
          @select="fillFromLocation"
        />
        <InputText
          id="attention"
          autocomplete="one-time-code"
          :name="`${mode}.attention`"
          :label="t('attention_name')"
          :placeholder="t('attention_name')"
        />
      </template>
      <InputText
        v-else
        id="name"
        autocomplete="one-time-code"
        class="col-span-2"
        :name="`${mode}.name`"
        :label="t('name')"
        :placeholder="t('name')"
      />
      <InputAddressGoogle
        id="street1"
        :name="`${mode}.street1`"
        :label="t('street1')"
        :placeholder="t('address')"
        :country="country"
        @select="fillFromLocation"
      />
      <InputText
        id="street2"
        autocomplete="one-time-code"
        :name="`${mode}.street2`"
        :label="t('street2_optional')"
        :placeholder="t('address')"
      />
      <div class="col-span-2 flex justify-between gap-3">
        <InputText
          id="zip_code"
          autocomplete="one-time-code"
          class="w-full"
          :name="`${mode}.zip_code`"
          :label="t('zip_code')"
          :placeholder="t('zip_code')"
          @update:model-value="autocity"
        />
        <InputText
          id="city"
          autocomplete="one-time-code"
          class="w-full"
          :name="`${mode}.city`"
          :label="t('city')"
          :placeholder="t('city')"
        />
      </div>
      <InputText
        id="email"
        autocomplete="one-time-code"
        :name="`${mode}.email`"
        :label="t('email')"
        :placeholder="t('email')"
      />
      <InputPhone
        id="phone"
        :set-prefix="setPrefix"
        :name="`${mode}.phone`"
        :label="t('phone')"
        :placeholder="t('phone')"
      />
    </div>
    <div class="mt-5">
      <SelectCheckbox id="save_receiver" :model-value="false" :title="t('save_receiver')" :name="`save_${mode}`" />
    </div>
    <AddressModal
      v-if="addressModalOpen"
      :open="addressModalOpen"
      :mode="mode"
      :values="values"
      :type="type"
      @close="addressModalOpen = false"
      @update-address="(val) => updateAddress(val)"
    />
  </div>
</template>

<script setup lang="ts">
  import type { GoogleAddressData } from "~/components/Input/AddressGoogle.vue";

  import AddressModal from "./addressModal.vue";

  const yup = useYup();
  const { t } = useI18n();
  const selectedContact = ref(null);
  const { isDFM } = useWorkspacesStore();

  const emit = defineEmits(["emitContact", "updateCity", "updateAddress", "updateValidation"]);

  const debounceTimeout = ref<NodeJS.Timeout>();

  const addressModalOpen = ref(false);

  const setPrefix = ref("DK");

  const props = withDefaults(
    defineProps<{
      values?: any;
      mode: "receiver" | "sender";
      type?: "default" | "consignments" | "returns";
    }>(),
    {
      values: [],
      type: "default",
    }
  );

  const showBusinessToggle = computed(() => {
    if (props.mode === "sender") {
      return !isDFM;
    } else if (props.type == "consignments") {
      return false;
    } else {
      return true;
    }
  });

  const isBusiness = computed(() => {
    if (props.mode === "receiver") {
      return props.values.receiver?.business;
    } else {
      return props.values.sender?.business;
    }
  });

  const country = computed(() => {
    if (props.mode === "receiver") {
      return props.values.receiver?.country;
    } else {
      return props.values.sender?.country;
    }
  });

  const reverseMode = computed(() => {
    if (props.mode === "receiver") {
      return "sender";
    } else {
      return "receiver";
    }
  });

  const debounce = () => {
    if (debounceTimeout.value) clearTimeout(debounceTimeout.value);
    debounceTimeout.value = setTimeout(() => {
      autocity();
    }, 300); // delay for half second
  };

  watch(
    () => props?.values[props.mode]?.zip_code,
    (value) => {
      if (value) {
        debounce();
      }
    }
  );

  watch(
    () => props?.values[props.mode]?.country,
    (value) => {
      if (value) {
        setPrefix.value = value;
      }
    }
  );

  const autocity = () => {
    if (!props.values[props.mode]) return;
    if (!props.values[props.mode]?.zip_code || !props.values[props.mode]?.country) return;

    homeFetch(
      `/autocity/?zip_code=${props.values[props.mode]?.zip_code}&country=${props.values[props.mode]?.country}`
    ).then((response) => {
      if (response.data.autocity.length > 0) {
        emit("updateCity", response.data.autocity[0].city);
      }
    });
  };

  const updateAddress = (val: object) => {
    addressModalOpen.value = false;
    emit("updateAddress", { data: { ...val, company_name: val.name } });
  };

  watch(
    () => selectedContact.value,
    (value) => {
      if (value) {
        const contact = contacts.value.find((contact) => contact.value === value);
        if (contact) {
          contact.data.phone = contact.data.phone_with_prefix;
          emit("emitContact", contact);
        }
      }
    }
  );

  const fillFromLocation = (data?: GoogleAddressData) => {
    const address = {
      attention: props.values[props.mode]?.attention,
      business: !!data?.businessStatus,
      city: data?.geoLocation?.city || props.values[props.mode]?.city,
      company_name: data?.businessStatus ? data.displayName?.text : null,
      country_code: data?.geoLocation?.country_code?.toUpperCase() || props.values[props.mode]?.country,
      email: props.values[props.mode]?.email,
      name: props.values[props.mode]?.name,
      phone: props.values[props.mode]?.phone,
      phone_prefix: props.values[props.mode]?.phone_prefix,
      street1: data?.geoLocation?.address_line1 || "",
      zip_code: data?.geoLocation?.postcode || props.values[props.mode]?.zip_code,
    };
    emit("emitContact", {
      data: address,
    });
  };

  const { data: contacts, pending: loadingContacts, deleteContact } = await useContacts();
  const { countries } = storeToRefs(useCountriesStore());

  // #region State handeling
  const stateSettings = computed<State | undefined>(() => {
    if (!country.value || !countries.value) return;
    const countryData = countries.value?.find((c) => c.value === country.value);
    if (!countryData?.data?.fields?.state) return;
    return countryData.data?.fields?.state;
  });

  watch(stateSettings, (value) => {
    emit("updateValidation", {
      fields: {
        ["state"]: value?.required
          ? yup
              .string()
              .nullable()
              .required(t("is_required_field", { entity: value.label }))
          : yup.string().nullable(),
      },
      step: 0,
    });
  });
  // #endregion

  // #region Zipcode handeling
  const zipCodeValidation = computed(() => {
    return country.value && !["AE", "CN", "HK"].includes(country.value);
  });

  watch(zipCodeValidation, (value) => {
    emit("updateValidation", {
      fields: {
        ["zip_code"]: value
          ? yup
              .string()
              .nullable()
              .required(t("is_required_field", { entity: t("zip_code") }))
          : yup.string().nullable(),
      },
      step: 0,
    });
  });
</script>
